import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { MoveDown, MoveUp, Trash, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

export type ProductImageType = {
	id?: string;
	product_id?: string;
	image_url: string;
	is_primary?: boolean;
	sequence?: number;
	file?: File; // For new local files
	isNew?: boolean; // To distinguish between existing and new files
};

interface ProductImageManagerProps {
	selectedFiles: File[];
	setSelectedFiles: (files: File[]) => void;
	isUploading: boolean;
	uploadProgress: number;
	onReorder: (images: ProductImageType[]) => Promise<void>;
}

export function ProductImageManager({
	selectedFiles,
	setSelectedFiles,
	isUploading,
	uploadProgress,
	onReorder,
}: ProductImageManagerProps) {
	const [orderedImages, setOrderedImages] = useState<ProductImageType[]>([]);

	// Create object URLs for selected files with unique keys
	const fileUrls = useMemo(() => {
		return selectedFiles.reduce((acc, file, index) => {
			// Use file name and last modified time to create a unique key
			const key = `${file.name}-${file.lastModified}`;
			acc[key] = URL.createObjectURL(file);
			return acc;
		}, {} as Record<string, string>);
	}, [selectedFiles]);

	// Cleanup object URLs on unmount
	useEffect(() => {
		return () => {
			Object.values(fileUrls).forEach(URL.revokeObjectURL);
		};
	}, [fileUrls]);

	// Update orderedImages when selectedFiles change
	useEffect(() => {
		const newImages = selectedFiles.map((file, index) => {
			const key = `${file.name}-${file.lastModified}`;
			return {
				image_url: fileUrls[key],
				file,
				isNew: true,
				sequence: index,
				is_primary: index === 0,
				// Add a unique identifier for each image
				id: key,
			};
		});
		setOrderedImages(newImages);
	}, [selectedFiles, fileUrls]);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;
			if (files) {
				// Validate file types and sizes
				const validFiles = Array.from(files).filter((file) => {
					const isValidType = [
						"image/jpeg",
						"image/png",
						"image/webp",
					].includes(file.type);
					const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
					return isValidType && isValidSize;
				});

				if (validFiles.length !== files.length) {
					toast({
						title: "Invalid files",
						description:
							"Some files were skipped. Please ensure all files are images under 5MB.",
						variant: "destructive",
					});
				}

				setSelectedFiles([...selectedFiles, ...validFiles]);
			}
			e.target.value = "";
		},
		[selectedFiles, setSelectedFiles]
	);

	const moveImage = useCallback(
		async (index: number, direction: "up" | "down") => {
			if (
				(direction === "up" && index === 0) ||
				(direction === "down" && index === orderedImages.length - 1)
			) {
				return;
			}

			const newOrderedImages = [...orderedImages];
			const swapIndex = direction === "up" ? index - 1 : index + 1;

			// Swap the images
			[newOrderedImages[index], newOrderedImages[swapIndex]] = [
				newOrderedImages[swapIndex],
				newOrderedImages[index],
			];

			// Update sequences and primary status
			const resequencedImages = newOrderedImages.map((img, idx) => ({
				...img,
				sequence: idx,
				is_primary: idx === 0,
			}));

			// Update local state
			setOrderedImages(resequencedImages);

			// Update selected files to match new order
			const reorderedFiles = resequencedImages
				.map((img) =>
					selectedFiles.find(
						(file) => `${file.name}-${file.lastModified}` === img.id
					)
				)
				.filter((file): file is File => file !== undefined);

			setSelectedFiles(reorderedFiles);
			await onReorder(resequencedImages);
		},
		[orderedImages, onReorder, selectedFiles, setSelectedFiles]
	);

	const handleDelete = useCallback(
		(index: number) => {
			const imageToDelete = orderedImages[index];

			// Remove from selected files
			const newFiles = selectedFiles.filter(
				(file) =>
					`${file.name}-${file.lastModified}` !== imageToDelete.id
			);
			setSelectedFiles(newFiles);

			// Update ordered images
			const newOrderedImages = orderedImages
				.filter((_, idx) => idx !== index)
				.map((img, idx) => ({
					...img,
					sequence: idx,
					is_primary: idx === 0,
				}));
			setOrderedImages(newOrderedImages);
		},
		[orderedImages, selectedFiles, setSelectedFiles]
	);

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-lg font-medium">Product Images</h3>
				<p className="text-sm text-gray-500">
					Upload high-quality images of your product. The first image
					will be used as the primary image.
				</p>
			</div>

			{/* Image Grid */}
			<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
				{orderedImages.map((image, index) => (
					<div
						key={image.id}
						className="relative group border rounded-md overflow-hidden">
						<img
							src={image.image_url}
							alt={`Product ${index + 1}`}
							className="w-full aspect-square object-cover"
						/>
						{index === 0 && (
							<span className="absolute top-2 left-2 bg-primary/10 text-primary px-2 py-1 rounded text-xs">
								Primary
							</span>
						)}
						<div className="absolute bottom-2 right-2 flex space-x-1">
							{index > 0 && (
								<Button
									type="button"
									size="icon"
									variant="secondary"
									className="h-8 w-8 opacity-90"
									onClick={() => moveImage(index, "up")}>
									<MoveUp className="h-4 w-4" />
								</Button>
							)}
							{index < orderedImages.length - 1 && (
								<Button
									type="button"
									size="icon"
									variant="secondary"
									className="h-8 w-8 opacity-90"
									onClick={() => moveImage(index, "down")}>
									<MoveDown className="h-4 w-4" />
								</Button>
							)}
							<Button
								type="button"
								size="icon"
								variant="destructive"
								className="h-8 w-8 opacity-90"
								onClick={() => handleDelete(index)}>
								<Trash className="h-4 w-4" />
							</Button>
						</div>
					</div>
				))}
			</div>

			{/* File Upload Button */}
			<div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
				<label className="cursor-pointer">
					<div className="flex flex-col items-center gap-2">
						<Upload className="h-8 w-8 text-gray-400" />
						<span className="font-medium">Upload Images</span>
						<span className="text-xs text-gray-500">
							PNG, JPG, WEBP up to 5MB
						</span>
					</div>
					<input
						type="file"
						accept="image/*"
						multiple
						onChange={handleFileChange}
						className="hidden"
						disabled={isUploading}
					/>
				</label>
			</div>

			{/* Upload Progress */}
			{isUploading && (
				<div className="w-full bg-gray-200 rounded-full h-2.5">
					<div
						className="bg-primary h-2.5 rounded-full"
						style={{ width: `${uploadProgress}%` }}
					/>
				</div>
			)}
		</div>
	);
}
